'use client';

import { usePathname } from 'next/navigation';
import { AppShell } from '@/components/shell';
import { useMondayAuth } from '@/contexts/MondayAuthContext';
import { AuthLoadingScreen } from '@/components/auth/AuthLoadingScreen';
import { ViewOnlyScreen } from '@/components/auth/ViewOnlyScreen';
import { ConnectScreen } from '@/components/auth/ConnectScreen';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';
import { isOnboardingCompleted } from '@/lib/utils/onboarding';

// Routes that manage their own padding/layout
const noPaddingRoutes = ['/bulk-operations', '/csv-import', '/recurring-patterns'];

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { isLoading, error, errorSource, authSteps, retryState, refreshAuth, user, organization } = useMondayAuth();
  const shouldHavePadding = !noPaddingRoutes.some(route => pathname.startsWith(route));

  // Show loading/retry/error states using AuthLoadingScreen
  if (isLoading || error || retryState?.isRetrying) {
    return (
      <AuthLoadingScreen
        error={error}
        errorSource={errorSource}
        authSteps={authSteps}
        retryState={retryState}
        onRetry={refreshAuth}
      />
    );
  }

  // Block view-only users — they cannot use the monday.com API
  if (user?.is_view_only) {
    return <ViewOnlyScreen />;
  }

  // Block users without an OAuth token — operations will fail without one
  if (organization?.oauthTokenStatus === 'missing') {
    return <ConnectScreen variant="connect" />;
  }
  if (organization?.oauthTokenStatus === 'expired') {
    return <ConnectScreen variant="reconnect" />;
  }

  // Show onboarding flow for new organizations
  if (organization && !isOnboardingCompleted(organization)) {
    return <OnboardingFlow organizationId={organization.id} />;
  }

  // Wrap content in AppShell - padding is conditional based on route
  return (
    <AppShell>
      <div className={shouldHavePadding ? 'p-6' : ''}>{children}</div>
    </AppShell>
  );
}
